import { Box, List, styled } from '@mui/material';
import { ReactElement, useMemo } from 'react';

import { Loader, useManifest } from '#shared';

import { useHubReportGroups } from '../model';

import { TableHeader } from './TableHeader';
import { TableRowGroupItem } from './TableRowGroupItem';

import type { TRegion } from '#entities';

const PREFIX = 'TSMED-WidgetHubsReport';
const widgetHubsReportClasses = {
  table: `${PREFIX}-table`,
  content: `${PREFIX}-content`,
};

const Root = styled(Box)(({ theme: { palette } }) => ({
  display: 'grid',
  width: '100%',
  height: '100%',
  gridTemplateColumns: '1fr',

  // Define consistent grid column widths used throughout the table
  // These CSS custom properties ensure alignment between header and rows
  '--hub-column-width': '120px',
  '--region-column-width': '35px',
  '--z-table-header': 20, // top sticky header
  '--z-sticky-left': 10, // left group header rail
  '--z-sticky-hub': 9, // first cell in each row
  '--z-cells': 1, // normal cells

  [`.${widgetHubsReportClasses.table}`]: {
    display: 'grid',
    gridTemplateRows: 'auto 1fr', // Header + scrollable content
    width: '100%',
    height: '100%', // Full viewport height to enable proper scrolling
    overflow: 'auto', // Prevent outer container from scrolling
    overscrollBehavior: 'none', //Prevent rubber-band / scroll chaining
    position: 'relative',
    isolation: 'isolate',
    [`.${widgetHubsReportClasses.content}`]: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      padding: 0,
      margin: 0,
      background: palette.mapBlue?.[800],
      width: 'max-content',
      minWidth: '100%',
    },
  },
}));

export const WidgetHubsReportContent = () => {
  useManifest();
  const hubGroups = useHubReportGroups();

  const groupItems = useMemo(() => {
    if (!hubGroups) {
      return;
    }
    return Object.keys(hubGroups).reduce<{
      groupItems: ReactElement[];
      startIndex: number;
    }>(
      (acc, groupKey) => {
        acc.groupItems.push(
          <TableRowGroupItem
            key={groupKey}
            rows={hubGroups[groupKey as TRegion]}
            region={groupKey}
            startIndex={acc.startIndex}
          />,
        );
        acc.startIndex += hubGroups[groupKey as TRegion].length;
        return acc;
      },
      { groupItems: [], startIndex: 0 },
    ).groupItems;
  }, [hubGroups]);

  return groupItems ? (
    <Root>
      <Box className={widgetHubsReportClasses.table}>
        <TableHeader />
        <List className={widgetHubsReportClasses.content}>{groupItems}</List>
      </Box>
    </Root>
  ) : (
    <Loader />
  );
};
